/**
 * שדות המידות בדף המוצר, לפי סוג תמחור — TASKS 4.2 · PRD 7.4.
 *
 * ⛔ ⛔ **אין מחיר כאן בשום מקום.** השדות אוספים כמות/מידות בלבד; החישוב
 *    מתבצע בשרת בלבד (7.4: "המחיר מחושב אך ורק בשרת").
 *
 * ⛔ **`per_sqm` הוסר כליל — Feedback1.md סעיף 10 (20.08.2026).** שני סוגי
 *    תמחור בלבד נשארו: `unit` ו-`per_meter`.
 */
import { useEffect, useId, useState } from 'react';
import type { CartMeasurements } from '../cart/CartContext.js';
import type { PricingType } from '../publicApi/catalogApi.js';

export interface PricingFieldsResult {
  measurements: CartMeasurements;
  isValid: boolean;
}

interface PricingFieldsProps {
  pricingType: PricingType;
  unitLabel: string | null;
  minOrderQty: string | null;
  qtyStep: string | null;
  onChange: (result: PricingFieldsResult) => void;
  /**
   * ⭐ TASKS 4.3 — עריכת שורת עגלה קיימת. כשמוגדר, הערכים ההתחלתיים הם אלה
   * ⛔ **ולא** ברירות המחדל הריקות (`qty=1`, אורך ריק). ⛔ ⛔ נקרא **פעם אחת
   * בלבד** בעלייה — עריכה אחרי מכן היא state פנימי של הרכיב, בדיוק כמו קודם.
   * `ProductPage` (4.2) לא מעביר את זה, ולכן ⛔ ההתנהגות שם לא משתנה כלל.
   */
  initialMeasurements?: CartMeasurements;
  /**
   * ⭐ Feedback1.md סעיף 13 (20.08.2026) — `MG-0001` ("גמיש למאייד נסתר")
   * נשאר `per_meter`, אבל ב-UI יש **שדה כמות יחיד במטרים** במקום שני שדות
   * "אורך נדרש"+"כמות" נפרדים (0.2ב: "בלי אורך נדרש"). מבחינת מנוע התמחור
   * זה עדיין `per_meter` רגיל — `qty` נשאר קבוע על 1 ו-`lengthM` הוא הערך
   * שהלקוח מזין, כך ש-`computeEffectiveQty` (שרת) מחזיר בדיוק את המטרים
   * שהוזנו. ⚠️ **דגל ייעודי לפי SKU בקוד ה-`ProductPage`, ⛔ לא מתג גנרי
   * בפאנל** — ראו הבהרה בסעיף 16.4 ב-Feedback1.md. ⭐ 24.08.2026 — `MG-0002`
   * ("מתאם גמיש למאייד נסתר") מצטרף לאותו דגל, מאותה סיבה בדיוק: מוצר מוגמר
   * של `MG-0001`, קיים באותן מידות, נמכר באותו אופן.
   */
  singleQuantityField?: boolean;
  /**
   * ⭐ 24.08.2026 — פידבק אריאנה: MG-0002 ("מתאם") הוא מוצר מוגמר, ⛔ לא
   * חומר גלם — התווית שלו היא **"כמות*"** בלבד, בלי המילה "מטרים" (בניגוד
   * ל-MG-0001, "כמות (מטרים)*"). מבחינת מנוע התמחור אין שינוי: עדיין
   * `lengthM` תחת הכיסוי (`per_meter`) — רק הניסוח שהלקוח רואה משתנה.
   */
  singleQuantityFieldLabel?: string;
}

function parsePositiveNumber(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function PricingFields({
  pricingType,
  unitLabel,
  minOrderQty,
  qtyStep,
  onChange,
  initialMeasurements,
  singleQuantityField = false,
  singleQuantityFieldLabel,
}: PricingFieldsProps) {
  const qtyId = useId();
  const lengthId = useId();
  const errorId = useId();

  const initialQtyRaw = String(initialMeasurements?.qty ?? 1);
  const initialLengthRaw = String(initialMeasurements?.lengthM ?? '');

  const [qtyRaw, setQtyRaw] = useState(initialQtyRaw);
  const [lengthRaw, setLengthRaw] = useState(initialLengthRaw);

  // ⭐ מדווח מצב התחלתי בעלייה, ומאפס בהחלפת סוג תמחור (מוצר אחר, בלי remount
  //    של הרכיב) — בלי זה "הוסף לעגלה" נשאר מנוטרל עד שהלקוח נוגע בשדה כלשהו,
  //    למרות שכמות ברירת המחדל (1) כבר תקינה עבור `unit`. ⭐ TASKS 4.3 —
  //    כשיש `initialMeasurements` (עריכת שורת עגלה), מאפס **אליהם** ולא לריק.
  useEffect(() => {
    setQtyRaw(initialQtyRaw);
    setLengthRaw(initialLengthRaw);
    emit(initialQtyRaw, initialLengthRaw);
    // ⚠️ תלוי בכוונה רק ב-pricingType — `emit`/הערכים ההתחלתיים נבנים מחדש
    //    בכל רינדור אבל ⛔ אינם צריכים להפעיל את האפקט הזה שוב
  }, [pricingType]);

  function emit(nextQtyRaw: string, nextLengthRaw: string) {
    if (pricingType === 'unit') {
      const nextQty = Number(nextQtyRaw);
      const nextQtyValid = Number.isInteger(nextQty) && nextQty > 0;
      onChange({ measurements: { qty: nextQty }, isValid: nextQtyValid });
      return;
    }

    // per_meter — ⭐ ב-singleQuantityField, qty נשאר תמיד 1 (לא מוצג ללקוח).
    const nextQty = singleQuantityField ? 1 : Number(nextQtyRaw);
    const nextQtyValid = Number.isInteger(nextQty) && nextQty > 0;
    const lengthM = parsePositiveNumber(nextLengthRaw);
    const min = minOrderQty !== null ? Number(minOrderQty) : null;
    const meetsMin = lengthM !== null && (min === null || lengthM >= min);
    onChange({
      measurements: { qty: nextQty, lengthM: lengthM ?? undefined },
      isValid: nextQtyValid && meetsMin,
    });
  }

  function handleQtyChange(value: string) {
    setQtyRaw(value);
    emit(value, lengthRaw);
  }
  function handleLengthChange(value: string) {
    setLengthRaw(value);
    emit(qtyRaw, value);
  }

  const lengthM = parsePositiveNumber(lengthRaw);
  const min = minOrderQty !== null ? Number(minOrderQty) : null;
  const belowMin = pricingType === 'per_meter' && lengthM !== null && min !== null && lengthM < min;

  const lengthFieldLabel = singleQuantityField
    ? (singleQuantityFieldLabel ?? 'כמות (מטרים)*')
    : 'אורך נדרש (מטרים)*';
  const lengthFieldErrorLabel = singleQuantityField
    ? singleQuantityFieldLabel
      ? 'כמות'
      : 'מטרים'
    : 'אורך';

  return (
    <div className="pricing-fields">
      {pricingType === 'per_meter' && (
        <div className="pricing-field">
          <label htmlFor={lengthId} className="block text-sm font-semibold">
            {lengthFieldLabel}
          </label>
          <input
            id={lengthId}
            type="number"
            inputMode="decimal"
            min={0}
            step={qtyStep ? Number(qtyStep) : 0.01}
            required
            aria-required="true"
            aria-invalid={belowMin ? 'true' : undefined}
            aria-describedby={belowMin ? errorId : undefined}
            value={lengthRaw}
            onChange={(event) => handleLengthChange(event.target.value)}
            className="mt-1 w-full max-w-40 rounded-md border border-line px-3 py-2"
          />
          {belowMin && (
            <p id={errorId} role="alert" className="text-danger mt-1 text-sm">
              {lengthFieldErrorLabel} מתחת למינימום ההזמנה - יש להזין לפחות {min} מטר.
            </p>
          )}
        </div>
      )}

      {!(pricingType === 'per_meter' && singleQuantityField) && (
        <div className="pricing-field mt-4">
          <label htmlFor={qtyId} className="block text-sm font-semibold">
            כמות*
          </label>
          <div className="mt-1 flex items-center gap-2">
            <input
              id={qtyId}
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              required
              aria-required="true"
              value={qtyRaw}
              onChange={(event) => handleQtyChange(event.target.value)}
              className="w-24 rounded-md border border-line px-3 py-2"
            />
            <span>{pricingType === 'unit' && unitLabel ? unitLabel : 'יחידות'}</span>
          </div>
        </div>
      )}
    </div>
  );
}
